import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { X, Upload, Loader2, ChevronDown, CheckCircle, AlertCircle } from 'lucide-react';
import { securityCommandControlLogApi } from '../services/api';
import type { ScclImportColumnMapping, ScclImportRowInput, ScclImportValidateResponse } from '../types';
import { SecurityCommandControlLogImportRowsPreviewTable } from './SecurityCommandControlLogImportRowsPreview';

type Step = 'upload' | 'mapping' | 'preview' | 'done';

const mappingStorageKey = (entryId: number) => `staffly.scclImport.mapping.v1.entry.${entryId}`;

const MAPPING_FIELDS: { key: keyof ScclImportColumnMapping; label: string; required: boolean }[] = [
  { key: 'incidentNo', label: 'Incident No', required: true },
  { key: 'incidentTime', label: 'Incident Time', required: false },
  { key: 'location', label: 'Location', required: false },
  { key: 'details', label: 'Details', required: false },
  { key: 'actionBy', label: 'Action By', required: false },
  { key: 'outcome', label: 'Outcome', required: false },
  { key: 'photosCctvDescription', label: 'Photos/CCTV Description', required: false },
];

type SheetData = {
  headers: string[];
  rows: Array<{ rowNumber: number; cells: Record<string, string> }>;
};

function parseMatrixToSheetData(matrix: (string | number | undefined)[][]): SheetData | null {
  if (!matrix.length) return null;
  const rawHeaders = matrix[0].map((c) => String(c ?? '').trim());
  if (!rawHeaders.some(Boolean)) return null;
  const headers = rawHeaders.filter(Boolean);

  const rows: Array<{ rowNumber: number; cells: Record<string, string> }> = [];
  let dataRowOrdinal = 0;
  for (let r = 1; r < matrix.length; r++) {
    const line = matrix[r];
    const cells: Record<string, string> = {};
    let any = false;
    rawHeaders.forEach((h, i) => {
      if (!h) return;
      const v = line[i];
      const cellStr = v === undefined || v === null ? '' : String(v).trim();
      cells[h] = cellStr;
      if (cellStr) any = true;
    });
    if (any) {
      dataRowOrdinal += 1;
      rows.push({ rowNumber: dataRowOrdinal, cells });
    }
  }
  return { headers, rows };
}

function pickMappedColumn(stored: string | undefined | null, headers: string[]): string {
  if (stored == null) return '';
  const t = String(stored).trim();
  if (t === '') return '';
  const hit = headers.find((h) => h === t);
  return hit !== undefined ? hit : '';
}

function intersectMappingWithHeaders(m: Partial<ScclImportColumnMapping>, headers: string[]): ScclImportColumnMapping {
  return {
    incidentNo: pickMappedColumn(m.incidentNo, headers),
    incidentTime: pickMappedColumn(m.incidentTime, headers),
    location: pickMappedColumn(m.location, headers),
    details: pickMappedColumn(m.details, headers),
    actionBy: pickMappedColumn(m.actionBy, headers),
    outcome: pickMappedColumn(m.outcome, headers),
    photosCctvDescription: pickMappedColumn(m.photosCctvDescription, headers),
  };
}

function saveStoredMapping(entryId: number, m: ScclImportColumnMapping) {
  try {
    localStorage.setItem(mappingStorageKey(entryId), JSON.stringify(m));
  } catch {
    /* ignore */
  }
}

function loadStoredMapping(entryId: number): Partial<ScclImportColumnMapping> {
  try {
    const raw = localStorage.getItem(mappingStorageKey(entryId));
    if (!raw) return {};
    return JSON.parse(raw) as Partial<ScclImportColumnMapping>;
  } catch {
    return {};
  }
}

export interface ImportSecurityCommandControlLogIncidentsModalProps {
  open: boolean;
  onClose: () => void;
  entryId: number;
  entryLabel?: string;
  onImportCommitted?: () => void;
}

export const ImportSecurityCommandControlLogIncidentsModal: React.FC<ImportSecurityCommandControlLogIncidentsModalProps> = ({
  open,
  onClose,
  entryId,
  entryLabel,
  onImportCommitted,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState('');
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheetsByName, setSheetsByName] = useState<Record<string, SheetData>>({});
  const [selectedSheetName, setSelectedSheetName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [sheetRows, setSheetRows] = useState<Array<{ rowNumber: number; cells: Record<string, string> }>>([]);
  const [mapping, setMapping] = useState<ScclImportColumnMapping>({
    incidentNo: '',
    incidentTime: '',
    location: '',
    details: '',
    actionBy: '',
    outcome: '',
    photosCctvDescription: '',
  });
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [validateResult, setValidateResult] = useState<ScclImportValidateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);

  const headerOptions = useMemo(() => ['', ...headers], [headers]);

  useEffect(() => {
    if (!open) return;
    const stored = loadStoredMapping(entryId);
    setMapping(intersectMappingWithHeaders(stored, []));
    setStep('upload');
    setFileName('');
    setSheetNames([]);
    setSheetsByName({});
    setSelectedSheetName('');
    setHeaders([]);
    setSheetRows([]);
    setValidateResult(null);
    setError(null);
    setSourceFile(null);
  }, [open, entryId]);

  const onSheetChange = useCallback(
    (sheetName: string) => {
      const data = sheetsByName[sheetName];
      if (!data) return;
      setSelectedSheetName(sheetName);
      setHeaders(data.headers);
      setSheetRows(data.rows);
      setMapping((prev) => intersectMappingWithHeaders(prev, data.headers));
    },
    [sheetsByName]
  );

  const handleFile = async (file: File) => {
    setError(null);
    setValidateResult(null);
    setSourceFile(file);
    setFileName(file.name);

    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const names = wb.SheetNames || [];
    const nextSheets: Record<string, SheetData> = {};
    names.forEach((n) => {
      const ws = wb.Sheets[n];
      if (!ws) return;
      const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true }) as (string | number | undefined)[][];
      const parsed = parseMatrixToSheetData(matrix);
      if (parsed) nextSheets[n] = parsed;
    });
    const sheetList = Object.keys(nextSheets);
    if (sheetList.length === 0) {
      setError('No usable sheets found (need a header row + at least one data row).');
      return;
    }
    setSheetsByName(nextSheets);
    setSheetNames(sheetList);

    // IMPORTANT: initialize selected sheet + headers/rows from the parsed workbook directly.
    // Calling onSheetChange() here would read from state (`sheetsByName`) before setSheetsByName applies.
    const first = sheetList[0];
    const firstData = nextSheets[first];
    setSelectedSheetName(first);
    setHeaders(firstData.headers);
    setSheetRows(firstData.rows);

    // Apply stored mapping only after headers exist (exact header match).
    const stored = loadStoredMapping(entryId);
    setMapping(intersectMappingWithHeaders(stored, firstData.headers));

    setStep('mapping');
  };

  const buildPayloadRows = (): ScclImportRowInput[] =>
    sheetRows.map((r) => ({
      rowNumber: r.rowNumber,
      cells: r.cells,
    }));

  const handleValidate = async () => {
    if (!mapping.incidentNo) {
      setError('Map Incident No to a spreadsheet column.');
      return;
    }

    saveStoredMapping(entryId, mapping);
    setValidating(true);
    setError(null);
    try {
      const res = await securityCommandControlLogApi.importValidate(
        entryId,
        {
          fileName: fileName || undefined,
          mapping: {
            incidentNo: mapping.incidentNo,
            incidentTime: mapping.incidentTime?.trim() || '',
            location: mapping.location?.trim() || '',
            details: mapping.details?.trim() || '',
            actionBy: mapping.actionBy?.trim() || '',
            outcome: mapping.outcome?.trim() || '',
            photosCctvDescription: mapping.photosCctvDescription?.trim() || '',
          },
          rows: buildPayloadRows(),
        },
        sourceFile
      );
      setValidateResult(res);
      setStep('preview');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Validation failed.');
    } finally {
      setValidating(false);
    }
  };

  const handleCommit = async () => {
    if (!validateResult?.batchId) return;
    setCommitting(true);
    setError(null);
    try {
      await securityCommandControlLogApi.importCommit(entryId, validateResult.batchId);
      setStep('done');
      onImportCommitted?.();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Import failed.');
    } finally {
      setCommitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="border-b">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900">Import incidents</h2>
              <p className="mt-0.5 truncate text-xs text-gray-500">
                Entry #{entryId}{entryLabel ? ` · ${entryLabel}` : ''}
              </p>
            </div>
            <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>
          {step === 'mapping' && sheetNames.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 bg-gray-50 px-4 py-2.5">
              <label htmlFor="import-sccl-sheet" className="text-sm font-medium text-gray-700">
                Sheet
              </label>
              <select
                id="import-sccl-sheet"
                value={selectedSheetName}
                onChange={(e) => onSheetChange(e.target.value)}
                className="min-w-[12rem] rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {sheetNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500">Columns and rows update from the selected sheet.</span>
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {error && (
            <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-4">
              <div className="rounded border border-gray-200 bg-gray-50 p-4">
                <div className="text-sm font-medium text-gray-900">Upload a file</div>
                <div className="mt-1 text-xs text-gray-600">
                  Upload an Excel file with a header row. You will map columns before importing.
                </div>
                <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                  <Upload className="h-4 w-4" />
                  Choose file
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) void handleFile(f);
                    }}
                  />
                </label>
              </div>
            </div>
          )}

          {step === 'mapping' && (
            <div className="space-y-4">
              <div className="rounded border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Column mapping</div>
                    <div className="mt-1 text-xs text-gray-600">
                      File: <span className="font-medium">{fileName || '—'}</span> · Rows detected: {sheetRows.length}
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {MAPPING_FIELDS.map((f) => (
                    <label key={f.key} className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-gray-800">
                        {f.label}
                        {f.required ? ' *' : ''}
                      </span>
                      <div className="relative">
                        <select
                          value={(mapping as any)[f.key] || ''}
                          onChange={(e) => setMapping((p) => ({ ...p, [f.key]: e.target.value }))}
                          className="w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 pr-10 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          {headerOptions.map((h) => (
                            <option key={h || '(none)'} value={h}>
                              {h || '(not mapped)'}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 'preview' && validateResult && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded bg-green-50 px-3 py-1.5 text-sm text-green-800">
                  <CheckCircle className="h-4 w-4" /> {validateResult.validRowCount} valid
                </span>
                <span className="inline-flex items-center gap-2 rounded bg-red-50 px-3 py-1.5 text-sm text-red-800">
                  <AlertCircle className="h-4 w-4" /> {validateResult.invalidRowCount} invalid
                </span>
              </div>
              <SecurityCommandControlLogImportRowsPreviewTable rows={validateResult.rows} />
            </div>
          )}

          {step === 'done' && (
            <div className="rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
              Import completed successfully.
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t px-4 py-3">
          <button
            type="button"
            className="rounded border border-gray-300 bg-white px-4 py-2 text-sm hover:bg-gray-50"
            onClick={onClose}
            disabled={validating || committing}
          >
            Close
          </button>

          <div className="flex items-center gap-2">
            {step === 'mapping' && (
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={() => void handleValidate()}
                disabled={validating}
              >
                {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Validate
              </button>
            )}
            {step === 'preview' && (
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={() => void handleCommit()}
                disabled={committing || !validateResult?.batchId}
              >
                {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Import
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

